import { AiFeature, OrganizationRoleType } from "../../autogen/bff-api";
import { orgAllowsFency } from "../ai/useAllowsFency";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import {
  canUpdateOrgEntry,
  hasContractCreateAccess,
  hasContractViewAccess,
  hasProjectCreateAccess,
  hasProjectViewAccess,
  hasSourcingCreateAccess,
  hasSourcingViewAccess,
  isCreator,
} from "../auth/utils";
import { isAppShellPath } from "../layout/isAppShellPath";
import { useViewAsRole } from "./viewAsRoleStore";

export const keepsReportsWith = (access: {
  canViewSourcingEvents: boolean;
  canViewContracts: boolean;
  viewsSharedContracts: boolean;
}): boolean => access.canViewSourcingEvents || access.canViewContracts || access.viewsSharedContracts;

export const useSub = () => {
  const authState = useLoggedInWithOrgContextState();
  const viewAsRole = useViewAsRole();

  if (!authState) {
    throw Error("Auth state with org context not available");
  }

  const org = authState.selectedOrg;
  const allowsFency = orgAllowsFency(org);
  const { hasBuyerAccess, hasContractAccess, hasProjectAccess } = org.subscription;
  const isSupplier = !(hasBuyerAccess || hasContractAccess || hasProjectAccess);
  const mayHaveAi = hasBuyerAccess || hasContractAccess;
  const hasAssistantAccess = mayHaveAi && org.subscription.hasAssistantAccess;
  const previewInScope = isAppShellPath(window.location.pathname);
  const previewedRole = !isSupplier && previewInScope && org.canEdit ? viewAsRole : null;

  const clampedByPreview = (grantedByBff: boolean, previewedRoleAllows: (roles: OrganizationRoleType[]) => boolean) =>
    grantedByBff && (previewedRole === null || previewedRoleAllows([previewedRole]));
  const unlessPreviewingARole = (grantedByBff: boolean) => grantedByBff && previewedRole === null;

  const viewsSourcingEvents = clampedByPreview(org.canViewSourcingEvents, hasSourcingViewAccess);
  const viewsContracts = clampedByPreview(org.canViewContracts, hasContractViewAccess);
  const canViewContracts = hasContractAccess && viewsContracts;
  const canViewContractOverview = clampedByPreview(org.canViewContractOverview, hasContractViewAccess);
  const canViewSourcingEvents = isSupplier || (hasBuyerAccess && viewsSourcingEvents);
  const viewsSharedContracts = isSupplier || (org.receivesContractShares && !canViewContracts);
  const canCreateEvents = hasBuyerAccess && clampedByPreview(org.canCreateEvent, hasSourcingCreateAccess);
  const canViewNeedsOverview = !isSupplier && clampedByPreview(org.canTakeNeeds, hasSourcingCreateAccess);
  const allowsAiFeature = (feature: AiFeature) => org.aiFeatures?.includes(feature) ?? false;

  return {
    canUpdateOrganization: unlessPreviewingARole(org.canEdit),
    canEditDocumentTemplates: unlessPreviewingARole(org.canEditDocumentTemplates),
    canConfigureContracts: unlessPreviewingARole(org.canConfigureContracts),
    canCreateContractTemplates: unlessPreviewingARole(org.canCreateContractTemplate),
    canViewSourcingEvents,
    canViewContracts,
    canViewContractOverview,
    canViewOverview: canViewContractOverview || (hasBuyerAccess && viewsSourcingEvents) || canViewNeedsOverview,
    canViewNeedsOverview,
    viewsSourcingEvents,
    viewsContracts,
    viewsSharedContracts,
    keepsReports: keepsReportsWith({ canViewSourcingEvents, canViewContracts, viewsSharedContracts }),
    canViewProjects: hasProjectAccess && clampedByPreview(org.canViewProjects, hasProjectViewAccess),
    canCreateProjects: clampedByPreview(org.canCreateProject, hasProjectCreateAccess),
    canReportNeeds: org.canReportNeed,
    canAssessNeeds: org.subscription.hasNeedsAccess && (isSupplier || viewsSourcingEvents),
    canUseContractSearch:
      allowsFency && org.subscription.hasFencyContractSearchAccess && hasContractAccess && viewsContracts,
    allowsFency,
    allowsAiFeature,
    hasContractAccess: org.subscription.hasContractAccess,
    hasBuyerAccess: org.subscription.hasBuyerAccess,
    isSupplier,
    canBid: !hasBuyerAccess,
    seesSupplierFrameworkAgreements: !hasBuyerAccess,
    hasSignatureAccess: org.subscription.hasSignatureAccess,
    hasBankIdAccess: org.subscription.hasBankIdAccess,
    canCreateEvents,
    canViewMarketDialogues: hasBuyerAccess && viewsSourcingEvents,
    canCreateMarketDialogues: canCreateEvents,
    canCreateContracts: clampedByPreview(org.canCreateContract, hasContractCreateAccess),
    canUpdateOrganizationEntries: clampedByPreview(org.canUpdateOrganizationEntries, canUpdateOrgEntry),
    canUpdateContactPersons: clampedByPreview(org.canUpdateContactPersons, canUpdateOrgEntry),
    canCreateOrganizationAssessments: clampedByPreview(org.canCreateOrganizationAssessments, isCreator),
    hasProductAccess: org.hasProductAccess,
    hasProjectAccess: org.hasProjectAccess,
    hasAiAccess: mayHaveAi && org.hasAiAccess,
    hasFencyContractSearchAccess: mayHaveAi && org.subscription.hasFencyContractSearchAccess,
    hasAssistantAccess,
    canUseAssistant: hasAssistantAccess && allowsAiFeature(AiFeature.Assistant),
    canChatWithDocuments: mayHaveAi && org.hasAiAccess && allowsAiFeature(AiFeature.DocumentReading),
    hasAiBidInsightAccess: org.subscription.hasAiBidInsightAccess,
    hasOutsideBidRegistrationAccess: org.subscription.hasOutsideBidRegistrationAccess,
    hasNeedsAccess: org.subscription.hasNeedsAccess,
    fencySyncEnabled: org.subscription.fencySyncEnabled,
    hasDocumentTemplateAccess: org.hasDocumentTemplateAccess,
    hasIFRSAccess: org.hasIFRSAccess,
    hasOrganizationAssessmentAccess: org.subscription.hasOrganizationAssessmentAccess,
    hasNegotiationAcces: org.subscription.hasNegotiationAccess,
  };
};

export type SubAccess = ReturnType<typeof useSub>;
